import { Alert, Button } from "@mui/material";
import React, { useMemo, useState } from "react";
import {
  ArchiveCmpMemberInput,
  CmpInvoicePaymentSurfaceStatusEnum,
  CmpMemberLifecycleStatusEnum,
} from "../../../graphql/types";
import { STAFF_SUPPORT_EMAIL } from "../../../utils/userFacingError";
import { commonStyles } from "../../common/commonStyles";
import { ActionSuccessDialog } from "../../common/dashboard/ActionSuccessDialog";
import MemberPastTransitionPaymentSurfaceFeedback, {
  getMemberPastTransitionRefundEvidence,
  getMemberPastTransitionPaymentSurfaceFeedback,
  MemberPastTransitionInvoiceEvidence,
  MemberPastTransitionRefundEvidence,
} from "./MemberPastTransitionPaymentSurfaceFeedback";

export type MemberPastTransitionCleanupReplayDetail = {
  generatedAtUtcMilli: number;
  lifecycleStatus: CmpMemberLifecycleStatusEnum;
  member: {
    chamberId: string;
    id: string;
    cmpExtension?: {
      archivedAtUtcMilli?: number | null;
      archiveReason?: string | null;
      refundRequired?: boolean | null;
      refundRequiredAmountInCents?: number | null;
      refundRequiredInvoiceId?: string | null;
    } | null;
  };
  invoices: ReadonlyArray<MemberPastTransitionInvoiceEvidence>;
};

export type MemberPastTransitionCleanupReplayResult = {
  actionSuccessful: boolean;
  detail: MemberPastTransitionCleanupReplayDetail;
};

export const getMemberPastTransitionCleanupReplayInput = (
  detail: MemberPastTransitionCleanupReplayDetail | null | undefined
): ArchiveCmpMemberInput | null => {
  if (
    !detail ||
    detail.lifecycleStatus !== CmpMemberLifecycleStatusEnum.Past ||
    detail.invoices.some(
      (invoice) =>
        invoice.cmpPastTransitionPaymentSurfaceStatus ===
        CmpInvoicePaymentSurfaceStatusEnum.ReconciliationRequired
    ) ||
    !detail.invoices.some(
      (invoice) =>
        invoice.cmpPastTransitionPaymentSurfaceStatus ===
        CmpInvoicePaymentSurfaceStatusEnum.Pending
    )
  ) {
    return null;
  }

  const archiveReason = detail.member.cmpExtension?.archiveReason?.trim();

  if (!detail.member.chamberId || !detail.member.id || !archiveReason) {
    return null;
  }

  const archivedAtUtcMilli =
    detail.member.cmpExtension?.archivedAtUtcMilli;

  return {
    chamberId: detail.member.chamberId,
    memberId: detail.member.id,
    archiveReason,
    ...(typeof archivedAtUtcMilli === "number" &&
    Number.isFinite(archivedAtUtcMilli)
      ? { archivedAtUtcMilli }
      : {}),
  };
};

/** The cleanup retry saved, but the member details did not reload. */
export const PAYMENT_CLEANUP_PAGE_NOT_UPDATED_TEXT =
  "The payment cleanup retry was saved, but the page did not update. Refresh the page before you do anything else for this member.";

type MemberPastTransitionCleanupReplayProps = {
  canReplayCleanup: boolean;
  detail: MemberPastTransitionCleanupReplayDetail;
  executeReplay: (
    input: ArchiveCmpMemberInput
  ) => Promise<MemberPastTransitionCleanupReplayResult>;
  onCommittedDetail?: (
    detail: MemberPastTransitionCleanupReplayDetail
  ) => Promise<string | undefined> | string | undefined;
  statusLabel?: "Deleted" | "Past";
};

const MemberPastTransitionCleanupReplay = ({
  canReplayCleanup,
  detail,
  executeReplay,
  onCommittedDetail,
  statusLabel,
}: MemberPastTransitionCleanupReplayProps) => {
  const [committedDetail, setCommittedDetail] =
    useState<MemberPastTransitionCleanupReplayDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{
    severity: "error" | "info" | "success";
    text: string;
  } | null>(null);
  const effectiveDetail =
    committedDetail &&
    committedDetail.generatedAtUtcMilli >= detail.generatedAtUtcMilli
      ? committedDetail
      : detail;
  const feedback = useMemo(
    () =>
      getMemberPastTransitionPaymentSurfaceFeedback(
        effectiveDetail.invoices
      ),
    [effectiveDetail.invoices]
  );
  const refundEvidence = useMemo(
    () =>
      getMemberPastTransitionRefundEvidence({
        invoices: effectiveDetail.invoices,
        memberExtension: effectiveDetail.member.cmpExtension,
      }),
    [effectiveDetail.invoices, effectiveDetail.member.cmpExtension]
  );
  const replayInput = useMemo(
    () => getMemberPastTransitionCleanupReplayInput(effectiveDetail),
    [effectiveDetail]
  );
  const pendingWithoutReplayIdentity =
    feedback?.severity === "info" && !replayInput;

  const handleReplay = async () => {
    if (!replayInput || loading) {
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const result = await executeReplay(replayInput);

      if (!result.actionSuccessful || !result.detail) {
        throw new Error("Chamber Pro did not confirm the cleanup replay.");
      }

      setCommittedDetail(result.detail);
      const nextFeedback = getMemberPastTransitionPaymentSurfaceFeedback(
        result.detail.invoices
      );
      let refreshWarning: string | undefined;

      try {
        refreshWarning = await onCommittedDetail?.(result.detail);
      } catch {
        refreshWarning = PAYMENT_CLEANUP_PAGE_NOT_UPDATED_TEXT;
      }

      if (nextFeedback?.severity === "success") {
        setMessage({
          severity: refreshWarning ? "info" : "success",
          text:
            refreshWarning ||
            "The old membership payment links are closed.",
        });
      } else if (nextFeedback?.severity === "warning") {
        setMessage({
          severity: "info",
          text:
            refreshWarning ||
            `We tried the payment cleanup again, but a payment for this member needs a manual check. Email ${STAFF_SUPPORT_EMAIL} for help.`,
        });
      } else {
        setMessage({
          severity: "info",
          text:
            refreshWarning ||
            "Old payment links for this member are still being closed. Wait a few minutes, then select Retry Payment Cleanup.",
        });
      }
    } catch {
      setMessage({
        severity: "error",
        text: `We could not confirm that the payment cleanup finished. Refresh the page to check it. Select Retry Payment Cleanup again only if the page still shows "Closing old payment links". If it keeps happening, email ${STAFF_SUPPORT_EMAIL}.`,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <MemberPastTransitionRefundEvidence
        evidence={refundEvidence}
        className={feedback ? "mb-3" : undefined}
        statusLabel={statusLabel}
      />
      <MemberPastTransitionPaymentSurfaceFeedback feedback={feedback} />
      {pendingWithoutReplayIdentity && (
        <Alert severity="warning" className="mt-3">
          We cannot retry the payment cleanup for this member. Refresh the
          page. If this message stays, email {STAFF_SUPPORT_EMAIL}.
        </Alert>
      )}
      {canReplayCleanup && replayInput && (
        <Button
          variant="contained"
          disabled={loading}
          onClick={() => void handleReplay()}
          sx={{
            backgroundColor: commonStyles.cpaGold,
            color: "white",
            fontWeight: 800,
            mt: 2,
            minWidth: 230,
            "&:hover": { backgroundColor: "#9b701d" },
          }}
        >
          {loading ? "Retrying Cleanup..." : "Retry Payment Cleanup"}
        </Button>
      )}
      {message && message.severity !== "success" && (
        <Alert severity={message.severity} className="mt-3" role="status">
          {message.text}
        </Alert>
      )}
      {/* A confirmed cleanup uses the design success pop-up, not a banner. */}
      <ActionSuccessDialog
        message={message?.severity === "success" ? message.text : null}
        onClose={() => setMessage(null)}
      />
    </div>
  );
};

export default MemberPastTransitionCleanupReplay;
